/**
 * Mapping helpers for the Letter Upload Eligible PA Statuses table. Rows are the
 * union of buckets across `pa_status_title_options` (alias + include-drug-name)
 * and `documents` (document type + filename template), so no configured bucket
 * is dropped on round-trip.
 */
import { v4 as uuidv4 } from "uuid";
import {
  LetterUploadDocument,
  LetterUploadDocumentConfig,
} from "../../../../../data-model/deliveryStepsConfig";
import { PaStatusRow } from "../shared/paStatusRows";

/**
 * Builds table rows from the Letter Upload document config by merging the title
 * options and the per-bucket documents.
 *
 * @param docConfig - Letter Upload document_config (may be undefined)
 * @returns Rows with bucket, alias, include-drug-name, document type + name
 */
export function letterUploadRowsFromConfig(
  docConfig: LetterUploadDocumentConfig | undefined,
): PaStatusRow[] {
  const titleOptions = docConfig?.pa_status_title_options ?? {};
  const documents = docConfig?.documents ?? {};
  const buckets = Array.from(
    new Set([...Object.keys(titleOptions), ...Object.keys(documents)]),
  );
  return buckets.map((bucket) => ({
    id: uuidv4(),
    bucket,
    alias: titleOptions[bucket]?.title_alias ?? "",
    includeDrugName: titleOptions[bucket]?.include_drug_name ?? false,
    documentType: documents[bucket]?.document_type ?? "",
    documentName: documents[bucket]?.filename_template ?? "",
  }));
}

/**
 * Rebuilds the `documents` map from table rows. Rows with a blank bucket, or
 * with neither a document type nor filename, are omitted (they carry no letter).
 *
 * @param rows - Current table rows
 * @returns Firestore bucket → document map
 */
export function rowsToDocuments(
  rows: PaStatusRow[],
): Record<string, LetterUploadDocument> {
  const result: Record<string, LetterUploadDocument> = {};
  for (const row of rows) {
    const bucket = row.bucket.trim();
    if (!bucket) continue;
    const documentType = (row.documentType ?? "").trim();
    const filenameTemplate = (row.documentName ?? "").trim();
    if (!documentType && !filenameTemplate) continue;
    result[bucket] = {
      document_type: documentType,
      filename_template: filenameTemplate,
    };
  }
  return result;
}
